"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Modal = Modal;
exports.ModalHeader = ModalHeader;
exports.ModalBody = ModalBody;
exports.ModalFooter = ModalFooter;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const utils_1 = require("@react-aria/utils");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_dom_1 = require("react-dom");
const components_1 = require("..");
const BeamContext_1 = require("../BeamContext");
const IconButton_1 = require("../IconButton");
const useModal_1 = require("./useModal");
const Css_1 = require("../../Css");
const hooks_1 = require("../../hooks");
const utils_2 = require("../../utils");
const ModalContext_1 = require("./ModalContext");
/**
 * Internal component for displaying a Modal; see `useModal` for the public API.
 *
 * Provides underlay, modal container, and header. Will disable scrolling of page under the modal.
 */
function Modal(props) {
    const { size = "md", content, forceScrolling, api, drawHeaderBorder = false, allowClosing = true } = props;
    const isFixedHeight = typeof size !== "string";
    const ref = (0, react_1.useRef)(null);
    const { modalBodyDiv, modalFooterDiv, modalHeaderDiv } = (0, BeamContext_1.useBeamContext)();
    const { closeModal } = (0, useModal_1.useModal)();
    const { overlayProps, underlayProps } = (0, react_aria_1.useOverlay)({
        ...props,
        isOpen: true,
        onClose: closeModal,
        isDismissable: true,
        shouldCloseOnInteractOutside: (el) => {
            // Do not close the Modal if the user is interacting with the Tribute mentions dropdown (via RichTextField) or with another 3rd party dialog (such as a lightbox) on top of it.
            return (allowClosing &&
                !(el.closest(".tribute-container") || el.closest("[role='dialog']") || el.closest("[role='alert']")));
        },
    }, ref);
    const { modalProps } = (0, react_aria_1.useModal)();
    const { dialogProps, titleProps } = (0, react_aria_1.useDialog)({ role: "dialog" }, ref);
    const [[width, height], setSize] = (0, react_1.useState)(getSize(size));
    const modalBodyRef = (0, react_1.useRef)(null);
    const modalFooterRef = (0, react_1.useRef)(null);
    const modalHeaderRef = (0, react_1.useRef)(null);
    const testId = (0, utils_2.useTestIds)({}, testIdPrefix);
    (0, react_aria_1.usePreventScroll)();
    const { sm } = (0, hooks_1.useBreakpoint)();
    if (api) {
        api.current = { setSize: (size = "md") => setSize(getSize(size)) };
    }
    const [hasScroll, setHasScroll] = (0, react_1.useState)(forceScrolling !== null && forceScrolling !== void 0 ? forceScrolling : false);
    (0, utils_1.useResizeObserver)({
        ref: modalBodyRef,
        onResize: (0, react_1.useCallback)(() => {
            const target = modalBodyRef.current;
            if (forceScrolling === undefined && !isFixedHeight) {
                setHasScroll(target.scrollHeight > target.clientHeight);
            }
        }, 
        // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []),
    });
    // Even though we use raw-divs for the createPortal calls, we do actually need to
    // use refs + useEffect to stitch those raw divs back into the React component tree.
    (0, react_1.useEffect)(() => {
        modalHeaderRef.current.appendChild(modalHeaderDiv);
        modalBodyRef.current.appendChild(modalBodyDiv);
        modalFooterRef.current.appendChild(modalFooterDiv);
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalBodyRef, modalFooterRef, modalHeaderRef]);
    return ((0, jsx_runtime_1.jsx)(ModalContext_1.ModalProvider, { children: (0, jsx_runtime_1.jsx)(react_aria_1.OverlayContainer, { children: (0, jsx_runtime_1.jsx)(components_1.AutoSaveStatusProvider, { children: (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.underlay.z4.$, ...underlayProps, ...testId.underlay, children: (0, jsx_runtime_1.jsx)(react_aria_1.FocusScope, { contain: true, restoreFocus: true, autoFocus: true, children: (0, jsx_runtime_1.jsxs)("div", { css: Css_1.Css.br24.bgWhite.bshModal.oh
                                .maxh("90vh")
                                .df.fdc.wPx(width)
                                .mhPx(defaultMinHeight)
                                .if(isFixedHeight)
                                .hPx(height)
                                .if(sm)
                                .add("height", "100dvh")
                                .add("width", "100dvw")
                                .maxh("none").br0.$, ref: ref, ...overlayProps, ...dialogProps, ...modalProps, ...testId, children: [(0, jsx_runtime_1.jsxs)("header", { css: Css_1.Css.df.fdrr.p3.fs0.if(drawHeaderBorder).bb.bcGray200.$, children: [(0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.fs0.pl1.$, children: allowClosing && (0, jsx_runtime_1.jsx)(IconButton_1.IconButton, { icon: "x", onClick: closeModal, ...testId.titleClose }) }), (0, jsx_runtime_1.jsx)("h1", { css: Css_1.Css.fg1.xl2Sb.gray900.$, ref: modalHeaderRef, ...titleProps, ...testId.title })] }), (0, jsx_runtime_1.jsx)("main", { ref: modalBodyRef, css: Css_1.Css.fg1.oya.if(hasScroll).bb.bcGray200.if(!!forceScrolling).oys.$, children: content }), (0, jsx_runtime_1.jsx)("footer", { css: Css_1.Css.fs0.$, children: (0, jsx_runtime_1.jsx)("div", { ref: modalFooterRef }) })] }) }) }) }) }) }));
}
function ModalHeader({ children }) {
    const { modalHeaderDiv } = (0, BeamContext_1.useBeamContext)();
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children }), modalHeaderDiv);
}
/** Provides consistent styling and the scrolling behavior for a modal's primary content. */
function ModalBody({ children, virtualized = false, }) {
    const { modalBodyDiv } = (0, BeamContext_1.useBeamContext)();
    const testId = (0, utils_2.useTestIds)({}, testIdPrefix);
    return (0, react_dom_1.createPortal)(
    // If `virtualized`, then we are expecting the `children` will handle their own scrollbar, so have the overflow hidden and adjust padding
    (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.h100.if(virtualized).oh.pl3.else.px3.$, ...testId.content, children: children }), modalBodyDiv);
}
/** Provides consistent styling for modal footers, i.e. where actions are placed. */
function ModalFooter({ children, xss, }) {
    const { modalFooterDiv } = (0, BeamContext_1.useBeamContext)();
    const testId = (0, utils_2.useTestIds)({}, testIdPrefix);
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)("div", { css: { ...Css_1.Css.p3.df.aic.jcfe.gap1.$, ...xss }, ...testId.footer, children: children }), modalFooterDiv);
}
const testIdPrefix = "modal";
const widths = {
    sm: 320,
    md: 480,
    lg: 640,
    xl: 800,
    xxl: 900,
};
const defaultMinHeight = 204;
function getSize(size) {
    if (typeof size === "string") {
        return [widths[size], defaultMinHeight];
    }
    else {
        return [widths[size.width], size.height];
    }
}
